import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import lodash from "lodash";
import queryString from "query-string";
import React from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { TestResult } from "../graphql/baseTypes";
import useQueryParams from "./useQueryParams";

export interface Filters {
  companyId?: string | null;
  locationId?: string | null;
  userId?: string | null;
  startDate?: MaterialUiPickersDate;
  endDate?: MaterialUiPickersDate;
  result?: TestResult;
}

export interface PageingState {
  page: number;
  rowsPerPage: number;
}

const initialPaginState: PageingState = {
  page: 0,
  rowsPerPage: 25,
};

interface FilterState {
  changed?: boolean;
  filtersApplie?: boolean;
  selected: Filters;
}
const initialFilterState: FilterState = {
  changed: false,
  filtersApplie: false,
  selected: {
    companyId: null,
    locationId: null,
    userId: null,
    endDate: null,
    startDate: null,
  },
};
interface Action {
  type: string;
}

interface SetAction extends Action {
  type: "SET_FILTERS";
  payload: Filters;
}
interface ResetAction extends Action {
  type: "RESET_FILTERS";
}

interface ApplieAction extends Action {
  type: "APPLY_FILTERS";
}
type Actions = SetAction | ResetAction | ApplieAction;

function reducer(state: FilterState, action: Actions) {
  switch (action.type) {
    case "SET_FILTERS":
      return { ...state, selected: { ...action.payload }, changed: true };
    case "RESET_FILTERS":
      return { ...initialFilterState };
    case "APPLY_FILTERS":
      return {
        ...state,
        changed: false,
        filtersApplie: true,
      };
    default:
      throw new Error();
  }
}

function useFilters(resetSelectedItems?: () => any) {
  const query = useQueryParams();
  const history = useHistory();
  const searchValue = query.get("q") || "";
  const { search: unparsedSearch } = useLocation<any>();
  const search = queryString.parse(unparsedSearch) || {};

  const getAppliedState = () => {
    const startDate = query.get("startDate");
    const endDate = query.get("endDate");
    const companyId = query.get("companyId");
    const userId = query.get("userId");
    const locationId = query.get("locationId");
    const result = query.get("result");

    const state: Filters = {
      ...(companyId && { companyId }),
      ...(userId && { userId }),
      ...(locationId && { locationId }),
      ...(startDate && { startDate: new Date(startDate) }),
      ...(endDate && { endDate: new Date(endDate) }),
      ...(result && { result: result as TestResult }),
    };
    return state;
  };

  const setSearchValue = (q: string) => {
    history.push({
      search: queryString.stringify({ ...search, q }),
    });
  };

  const getInitialState = () => {
    const current = getAppliedState();

    const state: FilterState = {
      selected: {
        ...initialFilterState.selected,
        ...current,
      },
      changed: false,
      filtersApplie: !lodash.isEmpty(current),
    };
    return state;
  };
  const [state, dispatch] = React.useReducer(reducer, getInitialState());

  const getPagingState = () => {
    const rowsPerPage = query.get("rowsPerPage");
    const page = query.get("page");

    const state: PageingState = {
      rowsPerPage: rowsPerPage
        ? Number(rowsPerPage)
        : initialPaginState.rowsPerPage,
      page: page ? Number(page) : initialPaginState.page,
    };
    return state;
  };

  const setQueryParams = React.useCallback(
    (q: Filters, paging: PageingState) => {
      const { endDate, startDate, locationId, companyId, userId } = q;
      const { page, rowsPerPage } = paging;
      history.push({
        search: queryString.stringify({
          ...(locationId && { locationId }),
          ...(companyId && { companyId }),
          ...(userId && { userId }),

          ...(startDate && { startDate: startDate.toISOString() }),
          ...(endDate && { endDate: endDate.toISOString() }),
          page,
          rowsPerPage,
          ...(searchValue && { q: searchValue }),
        }),
      });
    },
    [history, searchValue]
  );

  const resetQueryParams = React.useCallback(() => {
    history.push({
      search: queryString.stringify({
        ...initialPaginState,
        ...(searchValue && { q: searchValue }),
      }),
    });
  }, [history, searchValue]);

  const setPage = (page: number) => {
    setQueryParams(getAppliedState(), { ...getPagingState(), page });
    if (resetSelectedItems) {
      resetSelectedItems();
    }
  };

  const setRowsPerPage = (rows: number) => {
    setQueryParams(getAppliedState(), {
      page: initialPaginState.page,
      rowsPerPage: rows,
    });
    if (resetSelectedItems) {
      resetSelectedItems();
    }
  };
  const setSelection =
    (name: keyof Filters, optional?: keyof Filters) =>
    (value: any, optionalValue?: any) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          ...state.selected,
          [name]: value,
          ...(optional && { [optional]: optionalValue }),
        },
      });
    };
  const resetSelection =
    (name: keyof Filters, optional?: keyof Filters) => () => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          ...state.selected,
          [name]: null,
          ...(optional && { [optional]: null }),
        },
      });
    };
  const resetFilters = () => {
    dispatch({ type: "RESET_FILTERS" });
    resetQueryParams();
  };
  const applieFilters = () => {
    if (lodash.isEqual(state.selected, initialFilterState.selected)) {
      dispatch({ type: "RESET_FILTERS" });
      resetQueryParams();
    } else {
      dispatch({ type: "APPLY_FILTERS" });
      setQueryParams(state.selected, initialPaginState);
    }
  };

  return {
    state,
    appliedState: getAppliedState(),
    page: getPagingState(),
    setPage,
    setRowsPerPage,
    applieFilters,
    resetFilters,
    resetSelection,
    setSelection,
    searchValue,
    setSearchValue,
  };
}

export default useFilters;
